// A saved-items list kept in the visitor's own browser: no app, no account,
// no server round trip. One custom element drives every button on the page —
// product cards, the product page and the header count — so they all stay in
// step when any one of them is toggled.
const STORAGE_KEY = 'blanq:wishlist';
const CHANGED = 'blanq:wishlist:changed';

function read() {
  // Private windows and blocked site data both throw here rather than
  // returning empty, so every read has to survive failing.
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list.filter((h) => typeof h === 'string') : [];
  } catch {
    return [];
  }
}

function write(list) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    // Nothing to do — the toggle still works for this page view.
  }
  document.dispatchEvent(new CustomEvent(CHANGED, { detail: { list } }));
}

export function getWishlist() {
  return read();
}

class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.handle = this.getAttribute('handle');
    if (!this.button || !this.handle) return;

    this.onClick = this.onClick.bind(this);
    this.sync = this.sync.bind(this);
    this.button.addEventListener('click', this.onClick);
    document.addEventListener(CHANGED, this.sync);
    this.sync();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
    document.removeEventListener(CHANGED, this.sync);
  }

  onClick(event) {
    // On a card the button sits inside the product link.
    event.preventDefault();
    event.stopPropagation();
    const list = read();
    const next = list.includes(this.handle)
      ? list.filter((h) => h !== this.handle)
      : [this.handle, ...list];
    write(next);
  }

  sync() {
    const saved = read().includes(this.handle);
    this.button.setAttribute('aria-pressed', String(saved));
    const label = this.button.querySelector('[data-wishlist-label]');
    if (label) {
      label.textContent = saved
        ? this.getAttribute('label-saved') || 'Saved'
        : this.getAttribute('label-add') || 'Add to wishlist';
    }
    this.button.setAttribute(
      'aria-label',
      saved
        ? this.getAttribute('label-saved') || 'Saved'
        : this.getAttribute('label-add') || 'Add to wishlist'
    );
  }
}

class WishlistCount extends HTMLElement {
  connectedCallback() {
    this.sync = this.sync.bind(this);
    document.addEventListener(CHANGED, this.sync);
    this.sync();
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGED, this.sync);
  }

  sync() {
    const n = read().length;
    this.textContent = n ? ` (${n})` : '';
  }
}

// The wishlist page renders every product server side and hides what isn't
// saved, so the saved items are real theme product cards rather than a second
// card built in JavaScript.
class WishlistGrid extends HTMLElement {
  connectedCallback() {
    this.sync = this.sync.bind(this);
    document.addEventListener(CHANGED, this.sync);
    this.sync();
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGED, this.sync);
  }

  sync() {
    const saved = read();
    const grid = document.querySelector('.resource-list');
    const empty = this.querySelector('[data-wishlist-empty]');
    if (!grid) return;

    let shown = 0;
    grid.querySelectorAll('.resource-list__item, li').forEach((item) => {
      const link = item.querySelector('a[href*="/products/"]');
      const handle = link
        ? link.getAttribute('href').split('/products/')[1].split(/[?#]/)[0]
        : null;
      const keep = handle !== null && saved.includes(handle);
      item.hidden = !keep;
      if (keep) shown += 1;
    });

    const section = grid.closest('.shopify-section');
    if (section) section.hidden = shown === 0;
    if (empty) empty.hidden = shown !== 0;
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}
if (!customElements.get('wishlist-count')) {
  customElements.define('wishlist-count', WishlistCount);
}
if (!customElements.get('wishlist-grid')) {
  customElements.define('wishlist-grid', WishlistGrid);
}
